/**
 * SuiteRunner —— 单个套件的状态机。
 *
 * 设计要点：
 *   1. **isomorphic**：构造期接收一个已经 applyParams 完毕的 Suite；
 *      不持有任何 Node-only API（fs/path/Buffer/setTimeout 也不依赖
 *      宿主，timeout 由 caller 主动调用 tick(now) 推动）。
 *   2. **增量喂入**：caller 每收到一行 `LogcatLineView` 调用 `feed(line)`，
 *      runner 内部对所有规则评估并 emit `hit` / `verdict-changed` 事件。
 *   3. **命中索引**：每次喂入都会把 `(lineId -> hit[])` 写到 `hitsByLine`，
 *      `RunnerSet.hitsAt(lineId)` 直接拿现成结果，不必反向遍历。
 *   4. **finish() / tick(now)**：
 *      - tick(now) 推动 sequence 的 withinMs / afterWithinMs / 套件总
 *        timeoutMs。caller 可以在每次 feed 后用 `line.tsMs` 调一次
 *        tick；离线模式下也可以在文件读完后再 tick(lastTsMs + 1)。
 *      - finish() 把所有仍然 pending 的规则收尾为 timeout，emit finished。
 */

import type {
  Hit,
  LogcatLineView,
  Rule,
  RuleState,
  RuleVerdict,
  SequenceRule,
  Suite,
  SuiteEvent,
  SuiteSnapshot,
} from './types';
import { matchKeyword, type KeywordMatch } from './matchers/keyword';
import { describeStep, hitsForbidden, matchStep } from './matchers/sequence';

interface RuleStateInternal {
  rule: Rule;
  state: RuleState;
  hits: Hit[];
  /** sequence-only：当前等待的 step index。 */
  stepIndex: number;
  /** sequence-only：sequence 第一次命中第 0 step 的时刻；用于整体 withinMs。 */
  sequenceStartedAt?: number;
  /** sequence-only：上一 step 命中的时刻；用于 step 的 afterWithinMs。 */
  lastStepTsMs?: number;
  /** sequence-only：是否所有 step 都已命中。 */
  sequenceComplete: boolean;
}

export interface SuiteRunnerOptions {
  /** 调度器分配的 runId，用于事件区分。 */
  runId: string;
  /** 套件（已 applyParams）。 */
  suite: Suite;
  /** UI 收集到的参数原值，记到 snapshot 里方便展示与导出。 */
  params?: Record<string, string | number | boolean>;
  /** start tsMs；缺省取 Date.now()，离线模式下可注入文件首行 tsMs。 */
  startedAt?: number;
}

export type SuiteEventListener = (ev: SuiteEvent) => void;

export class SuiteRunner {
  private readonly runId: string;
  private readonly suite: Suite;
  private readonly params?: Record<string, string | number | boolean>;
  private readonly listeners = new Set<SuiteEventListener>();
  private readonly states: RuleStateInternal[];
  private readonly hitsByLine = new Map<number, Hit[]>();
  private readonly startedAt: number;
  private finishedAt?: number;
  private linesFed = 0;
  private finished = false;

  constructor(opts: SuiteRunnerOptions) {
    this.runId = opts.runId;
    this.suite = opts.suite;
    this.params = opts.params;
    this.startedAt = opts.startedAt ?? Date.now();
    this.states = opts.suite.rules.map((rule) => ({
      rule,
      state: rule.disabled ? 'pending' : 'pending',
      hits: [],
      stepIndex: 0,
      sequenceComplete: false,
    }));
  }

  on(listener: SuiteEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getRunId(): string {
    return this.runId;
  }

  getSuiteId(): string {
    return this.suite.id;
  }

  isFinished(): boolean {
    return this.finished;
  }

  /** 喂一行日志；线程不安全，调用方需保证串行。 */
  feed(line: LogcatLineView): void {
    if (this.finished) return;
    this.linesFed += 1;
    for (const st of this.states) {
      if (st.rule.disabled) continue;
      this.evaluateRule(st, line);
    }
    // tick by line.tsMs 以推动 timeout / withinMs。
    this.tick(line.tsMs);
  }

  /** 推动时钟；caller 可以在 feed 之外的空闲时刻定期调用以触发 timeout。 */
  tick(nowMs: number): void {
    if (this.finished) return;
    // 套件级 timeoutMs：从 startedAt 起算
    const overall = this.suite.timeoutMs ?? 0;
    if (overall > 0 && nowMs - this.startedAt >= overall) {
      // 把所有仍 pending 的规则置 timeout
      for (const st of this.states) {
        if (st.state === 'pending' && !st.rule.disabled) {
          this.markTimeout(st, nowMs, '套件总超时');
        }
      }
      // 同时把未完成的 sequence 也 timeout（sequence 内部仍可能在
      // sequenceComplete=false 但当前 step 还没命中）
    }
    // sequence 内部的 within / afterWithin
    for (const st of this.states) {
      if (st.rule.matcher !== 'sequence' || st.state !== 'pending' || st.rule.disabled) continue;
      const seq = st.rule as SequenceRule;
      if (st.stepIndex > 0) {
        const step = seq.steps[st.stepIndex];
        if (step?.afterWithinMs && st.lastStepTsMs !== undefined) {
          if (nowMs - st.lastStepTsMs >= step.afterWithinMs) {
            this.markTimeout(
              st,
              nowMs,
              `step \`${describeStep(seq, st.stepIndex)}\` 等待超过 ${step.afterWithinMs}ms`,
            );
            continue;
          }
        }
      }
      if (seq.withinMs && st.sequenceStartedAt !== undefined) {
        if (nowMs - st.sequenceStartedAt >= seq.withinMs) {
          this.markTimeout(st, nowMs, `sequence 总时限 ${seq.withinMs}ms 已到`);
        }
      }
    }
  }

  /**
   * 收尾。所有 pending 规则置 timeout 并触发 `finished` 事件。
   * 离线扫描 / CLI 多套件并行结束时调用。
   */
  finish(nowMs?: number): void {
    if (this.finished) return;
    const t = nowMs ?? Date.now();
    for (const st of this.states) {
      if (st.state === 'pending' && !st.rule.disabled) {
        this.markTimeout(st, t, '扫描结束时仍未命中');
      }
    }
    this.finishedAt = t;
    this.finished = true;
    this.emit({ type: 'finished', runId: this.runId, suiteId: this.suite.id, snapshot: this.snapshot() });
  }

  /** 即时快照，UI 渲染单张卡片。 */
  snapshot(): SuiteSnapshot {
    return {
      runId: this.runId,
      suiteId: this.suite.id,
      suiteName: this.suite.name,
      params: this.params,
      linesFed: this.linesFed,
      overallState: this.computeOverallState(),
      running: !this.finished,
      startedAt: this.startedAt,
      finishedAt: this.finishedAt,
      verdicts: this.states.map((st) => this.toVerdict(st)),
    };
  }

  /** 根据 lineId 查所有命中（含本套件的多个规则）。 */
  hitsAt(lineId: number): Hit[] {
    return this.hitsByLine.get(lineId) ?? [];
  }

  /* -------------------- 内部：规则评估 -------------------- */

  private evaluateRule(st: RuleStateInternal, line: LogcatLineView): void {
    if (st.state !== 'pending') {
      // keyword 即便已经 pass 也允许累计 hitCount；fail/timeout 不再变。
      if (st.state === 'pass' || st.state === 'mid') {
        if (st.rule.matcher === 'keyword') {
          const m = matchKeyword(st.rule, line);
          if (m.hit) this.recordHit(st, line, st.state, undefined, m);
        }
      }
      return;
    }
    switch (st.rule.matcher) {
      case 'keyword': {
        const m = matchKeyword(st.rule, line);
        if (m.hit) {
          this.transitionRule(st, st.rule.severity, line, undefined, m);
        }
        break;
      }
      case 'sequence': {
        this.evaluateSequence(st, line);
        break;
      }
    }
  }

  private evaluateSequence(st: RuleStateInternal, line: LogcatLineView): void {
    const seq = st.rule as SequenceRule;
    const step = seq.steps[st.stepIndex];
    if (!step) return;
    // forbidden 优先：哪怕本行同时命中 step，forbidden 也优先 fail
    if (hitsForbidden(step, line)) {
      this.transitionRule(
        st,
        'fail',
        line,
        `step \`${describeStep(seq, st.stepIndex)}\` 出现 forbidden 关键字`,
      );
      return;
    }
    // 时间窗内未命中 step
    if (step.afterWithinMs && st.lastStepTsMs !== undefined) {
      if (line.tsMs - st.lastStepTsMs >= step.afterWithinMs) {
        this.markTimeout(
          st,
          line.tsMs,
          `step \`${describeStep(seq, st.stepIndex)}\` 等待超过 ${step.afterWithinMs}ms`,
        );
        return;
      }
    }
    const stepMatch = matchStep(step, line);
    if (stepMatch.hit) {
      const isFirst = st.stepIndex === 0;
      const wasLast = st.stepIndex === seq.steps.length - 1;
      if (isFirst) st.sequenceStartedAt = line.tsMs;
      st.lastStepTsMs = line.tsMs;
      st.stepIndex += 1;
      if (wasLast) {
        st.sequenceComplete = true;
        this.transitionRule(st, st.rule.severity, line, undefined, stepMatch);
      } else {
        // 中间 step 命中：只记 hit，但 state 仍为 pending
        this.recordHit(
          st,
          line,
          'pass',
          `step ${st.stepIndex - 1} matched`,
          stepMatch,
        );
      }
    }
  }

  private transitionRule(
    st: RuleStateInternal,
    nextState: 'pass' | 'mid' | 'fail',
    line: LogcatLineView,
    reason?: string,
    match?: KeywordMatch,
  ): void {
    st.state = nextState;
    this.recordHit(st, line, nextState, reason, match);
    this.emit({
      type: 'verdict-changed',
      runId: this.runId,
      suiteId: this.suite.id,
      verdict: this.toVerdict(st),
    });
  }

  private markTimeout(st: RuleStateInternal, tsMs: number, reason: string): void {
    st.state = 'timeout';
    const hit: Hit = {
      ruleId: st.rule.id,
      state: 'timeout',
      lineId: -1,
      ts: '',
      tsMs,
      raw: '',
      summary: reason,
      reason,
      stepIndex: st.rule.matcher === 'sequence' ? st.stepIndex : undefined,
      sequenceComplete: st.sequenceComplete || undefined,
    };
    st.hits.push(hit);
    this.emit({
      type: 'verdict-changed',
      runId: this.runId,
      suiteId: this.suite.id,
      verdict: this.toVerdict(st),
    });
  }

  private recordHit(
    st: RuleStateInternal,
    line: LogcatLineView,
    state: Exclude<RuleState, 'pending'>,
    reason?: string,
    match?: KeywordMatch,
  ): void {
    const summary = `${line.tag ? `${line.tag}: ` : ''}${(line.msg || line.raw).slice(0, 200)}`;
    const hit: Hit = {
      ruleId: st.rule.id,
      state,
      lineId: line.lineId,
      ts: line.ts,
      tsMs: line.tsMs,
      raw: line.raw,
      summary,
      reason,
      stepIndex: st.rule.matcher === 'sequence' ? Math.max(0, st.stepIndex - 1) : undefined,
      sequenceComplete: st.sequenceComplete || undefined,
      mode: match?.mode,
      fuzzyKind: match?.fuzzyKind,
      matchedKeyword: match?.matchedKeyword,
      fuzzyLineTokens: match?.fuzzyLineTokens,
      fuzzyWindowStart: match?.fuzzyWindowStart,
      fuzzyDiffs: match?.fuzzyDiffs,
      fuzzyDiffKinds: match?.fuzzyDiffKinds,
      fuzzyPattern: match?.fuzzyPattern,
    };
    st.hits.push(hit);
    const arr = this.hitsByLine.get(line.lineId) ?? [];
    arr.push(hit);
    this.hitsByLine.set(line.lineId, arr);
    this.emit({ type: 'hit', runId: this.runId, suiteId: this.suite.id, hit });
  }

  private toVerdict(st: RuleStateInternal): RuleVerdict {
    return {
      ruleId: st.rule.id,
      ruleName: st.rule.name,
      severity: st.rule.severity,
      state: st.state,
      lastHit: st.hits.length > 0 ? st.hits[st.hits.length - 1] : undefined,
      hitCount: st.hits.length,
      sequenceStepIndex: st.rule.matcher === 'sequence' ? st.stepIndex : undefined,
      sequenceComplete:
        st.rule.matcher === 'sequence' ? st.sequenceComplete : undefined,
    };
  }

  /**
   * 套件整体状态聚合：
   *
   * 规则的 `severity` 字段表达"命中后的结论"，但同时也隐含了"是否期望命中"：
   *   - severity=pass 的规则 = "我期望这件事发生"，pending = 还没看到 = 等待
   *   - severity=fail 的规则 = "这件事不该发生"，pending = 还没出错 = 安全
   *   - severity=mid 的规则 = "出现是个警告"，pending = 还没出现 = 安全
   *
   * 所以聚合规则：
   *   - 任一 state = fail / timeout → fail（已经踩坑）
   *   - 任一 state = mid → mid（出现了警告，但没崩）
   *   - 没有任何 fail / mid 命中：
   *       - 套件至少声明了 1 条 severity=pass 规则：
   *           * 若所有 severity=pass 规则都已 state=pass → pass（期望发生的都发生了）
   *           * 否则 → pending（还在等期望事件）
   *       - 套件没有 severity=pass 规则（纯防错套件）：
   *           * 一律 → pending（"持续监测中"，避免误报"通过"）
   *   - disabled 规则全程忽略。
   *
   * 这样用户写"冷启动 = 必须出现 X / 不该出现 Y"时，X 出现 + Y 一直没出现就
   * 会得到 pass；只声明 fail 规则在等错误的纯监测套件则始终保持 pending。
   */
  private computeOverallState(): RuleState {
    let hasMid = false;
    let totalPassSev = 0;
    let passedPassSev = 0;
    for (const st of this.states) {
      if (st.rule.disabled) continue;
      if (st.state === 'fail' || st.state === 'timeout') return 'fail';
      if (st.state === 'mid') hasMid = true;
      if (st.rule.severity === 'pass') {
        totalPassSev += 1;
        if (st.state === 'pass') passedPassSev += 1;
      }
    }
    if (hasMid) return 'mid';
    if (totalPassSev > 0 && passedPassSev === totalPassSev) return 'pass';
    return 'pending';
  }

  private emit(ev: SuiteEvent): void {
    for (const l of this.listeners) {
      try {
        l(ev);
      } catch {
        // listener 抛错绝不影响其它 listener
      }
    }
  }
}
